import { Component, type ErrorInfo, type ReactNode, useState, useCallback } from 'react'
import { ChevronDown, Copy, Check, RefreshCw, AlertTriangle } from 'lucide-react'

// ─── Error Context Collector ───────────────────────────────────────────────────

function collectErrorContext(error: Error, errorInfo?: ErrorInfo): string {
  const lines: string[] = [
    '=== Error Report ===',
    '',
    `Timestamp: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `User Agent: ${navigator.userAgent}`,
    '',
    '--- Error ---',
    `Name: ${error.name}`,
    `Message: ${error.message}`,
  ]

  if (error.cause) {
    lines.push(`Cause: ${String(error.cause)}`)
  }

  if (error.stack) {
    lines.push('', '--- Stack Trace ---', error.stack)
  }

  if (errorInfo?.componentStack) {
    lines.push('', '--- Component Stack ---', errorInfo.componentStack)
  }

  lines.push('', '=== End Report ===')
  return lines.join('\n')
}

// ─── Error Fallback UI ─────────────────────────────────────────────────────────

function ErrorFallback({ error, errorInfo, onReset }: {
  error: Error
  errorInfo?: ErrorInfo
  onReset: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const fullContext = collectErrorContext(error, errorInfo)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullContext)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for contexts without clipboard API
      const textarea = document.createElement('textarea')
      textarea.value = fullContext
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [fullContext])

  return (
    <div className="min-h-screen bg-[var(--color-cream,#F7F3E8)] flex items-center justify-center p-6">
      <div className="w-full max-w-[640px]">
        {/* Main card */}
        <div className="bg-white rounded-2xl border border-[var(--color-sand,#D4C5A9)] overflow-hidden" style={{ boxShadow: '0 4px 6px rgba(45, 45, 45, 0.07)' }}>
          {/* Error accent bar */}
          <div className="h-1 bg-[var(--color-error,#B55B5B)]" />

          <div className="p-8 sm:p-10">
            {/* Icon + heading */}
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[var(--color-error-light,#FAEAEA)] flex items-center justify-center shrink-0">
                <AlertTriangle size={24} className="text-[var(--color-error,#B55B5B)]" />
              </div>
              <div>
                <h1 className="font-serif text-2xl font-medium text-[var(--color-charcoal,#2D2D2D)] mb-1">
                  Something went wrong
                </h1>
                <p className="text-[var(--color-slate,#4A4A4A)] leading-relaxed">
                  The application hit an unexpected error. This has been noted. You can try refreshing, or if the problem persists, reach out to our team.
                </p>
              </div>
            </div>

            {/* Error summary (always visible) */}
            <div className="rounded-lg bg-[var(--color-cream,#F7F3E8)] border border-[var(--color-sand,#D4C5A9)] px-4 py-3 mb-6">
              <p className="text-sm font-mono text-[var(--color-error,#B55B5B)] break-all">
                {error.name}: {error.message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                type="button"
                onClick={onReset}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-[var(--color-charcoal,#2D2D2D)] text-white transition-all hover:brightness-125 cursor-pointer"
              >
                <RefreshCw size={15} />
                Try Again
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg border border-[var(--color-sand,#D4C5A9)] text-[var(--color-charcoal,#2D2D2D)] bg-white transition-all hover:bg-[var(--color-cream,#F7F3E8)] cursor-pointer"
              >
                {copied ? <Check size={15} className="text-[var(--color-success,#5D8A66)]" /> : <Copy size={15} />}
                {copied ? 'Copied!' : 'Copy Error Report'}
              </button>
            </div>

            {/* Technical Details - Expandable */}
            <div className="border-t border-[var(--color-sand,#D4C5A9)] pt-4">
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-2 text-sm font-medium text-[var(--color-gray,#6B6B6B)] hover:text-[var(--color-charcoal,#2D2D2D)] transition-colors cursor-pointer w-full text-left"
              >
                <ChevronDown
                  size={16}
                  className="transition-transform duration-200"
                  style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
                Technical Details
              </button>

              {expanded && (
                <div className="mt-4 space-y-4 animate-[fadeIn_0.2s_ease-out]">
                  {/* Stack trace */}
                  {error.stack && (
                    <div>
                      <p className="text-xs font-semibold text-[var(--color-gray,#6B6B6B)] uppercase tracking-wider mb-2">
                        Stack Trace
                      </p>
                      <pre className="text-xs font-mono leading-relaxed text-[var(--color-slate,#4A4A4A)] bg-[var(--color-charcoal,#2D2D2D)] text-[var(--color-sand,#D4C5A9)] rounded-lg p-4 overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap break-all">
                        {error.stack}
                      </pre>
                    </div>
                  )}

                  {/* Component stack */}
                  {errorInfo?.componentStack && (
                    <div>
                      <p className="text-xs font-semibold text-[var(--color-gray,#6B6B6B)] uppercase tracking-wider mb-2">
                        Component Stack
                      </p>
                      <pre className="text-xs font-mono leading-relaxed bg-[var(--color-charcoal,#2D2D2D)] text-[var(--color-sand,#D4C5A9)] rounded-lg p-4 overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap break-all">
                        {errorInfo.componentStack.trim()}
                      </pre>
                    </div>
                  )}

                  {/* Environment info */}
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-gray,#6B6B6B)] uppercase tracking-wider mb-2">
                      Environment
                    </p>
                    <div className="text-xs font-mono text-[var(--color-slate,#4A4A4A)] bg-[var(--color-cream,#F7F3E8)] rounded-lg p-4 space-y-1 border border-[var(--color-sand,#D4C5A9)]">
                      <p><span className="text-[var(--color-gray,#6B6B6B)]">URL:</span> {window.location.href}</p>
                      <p><span className="text-[var(--color-gray,#6B6B6B)]">Time:</span> {new Date().toISOString()}</p>
                      <p><span className="text-[var(--color-gray,#6B6B6B)]">UA:</span> {navigator.userAgent}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subtle footer */}
        <p className="text-center text-xs text-[var(--color-gray,#6B6B6B)] mt-6">
          If this keeps happening, email{' '}
          <a href="mailto:family@growinggenerations.com" className="text-[var(--color-navy,#1E3A5F)] no-underline hover:underline">
            family@growinggenerations.com
          </a>
          {' '}with the copied error report.
        </p>
      </div>
    </div>
  )
}

// ─── Route Error Component (for TanStack Router) ──────────────────────────────

export function RouteErrorComponent({ error, reset }: { error: Error; reset?: () => void }) {
  return (
    <ErrorFallback
      error={error}
      onReset={reset ?? (() => window.location.reload())}
    />
  )
}

// ─── Error Boundary (Class Component - React requirement) ──────────────────────

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: (props: { error: Error; errorInfo?: ErrorInfo; onReset: () => void }) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, errorInfo: null }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ error: null, errorInfo: null })
  }

  render() {
    const { error, errorInfo } = this.state

    if (error) {
      if (this.props.fallback) {
        return this.props.fallback({ error, errorInfo: errorInfo ?? undefined, onReset: this.handleReset })
      }
      return <ErrorFallback error={error} errorInfo={errorInfo ?? undefined} onReset={this.handleReset} />
    }

    return this.props.children
  }
}
